import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import './RCDetails.css';

function RCDetails({ vehicleNumber, shouldFetch, shouldAutoAddToGarage, onAutoAddComplete, isAuthenticated }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('Ownership Details'); // Open ownership by default
  const [rcData, setRcData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addingToGarage, setAddingToGarage] = useState(false);
  const [isAutoAdding, setIsAutoAdding] = useState(false);
  const [vehicleExistsInGarage, setVehicleExistsInGarage] = useState(false);

  // Masking utility functions
  const maskAlternateCharacters = (str) => {
    if (!str) return '';
    return str.split('').map((char, index) => index % 2 === 1 ? '*' : char).join('');
  };
  const maskLastFiveCharacters = (str) => {
    if (!str) return '';
    if (str.length <= 5) {
      return '*'.repeat(str.length);
    }
    return str.substring(0, str.length - 5) + '*'.repeat(5);
  };
  const maskFullString = (str) => {
    if (!str) return '';
    return '*'.repeat(str.length);
  };

  // Helper to safely access nested properties with fallback keys
  const getRCValue = (data, paths, defaultValue = 'N/A') => {
    if (!data) return defaultValue;
    for (const path of paths) {
      const keys = path.split('.');
      let value = data;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined || value === null) break;
      }
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return defaultValue;
  };

  // Step 1: POST /verify-rc to get request_id
  const startVerification = async (rcNumber) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication required. Please log in.");

    const headers = new Headers({ "Content-Type": "application/json" });
    headers.append("Authorization", `Bearer ${token}`);

    const response = await fetch("https://api.rtoxpert.com/api/rc/verify-rc", {
      method: "POST",
      headers,
      body: JSON.stringify({ rc_number: rcNumber }),
    });

    if (response.status === 401) throw new Error("Session expired. Please log in again.");
    if (!response.ok) throw new Error("Failed to start verification");

    const data = await response.json();
    if (data.data && data.data.rc_data) {
      return { immediateData: data.data.rc_data };
    }
    if (!data.request_id) throw new Error("Missing request_id from verification API");
    return data.request_id;
  };

  // Step 2: Poll GET /verification-status/:request_id until done
  const pollVerificationResult = async (requestId, maxTries = 10, intervalMs = 2000) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication required");

    const headers = new Headers({ "Content-Type": "application/json" });
    headers.append("Authorization", `Bearer ${token}`);

    for (let i = 0; i < maxTries; i++) {
      const response = await fetch(`https://api.rtoxpert.com/api/rc/verification-status/${requestId}`, {
        method: "GET",
        headers,
      });

      if (response.status === 401) throw new Error("Session expired");
      if (!response.ok) throw new Error("Failed to fetch verification status");

      const data = await response.json();

      if ((response.status === 200 || data.status === 200 || data.status === 'success' || data.status === 'completed') && (data.data?.rc_data || data.rc_data)) {
        return data.data?.rc_data || data.rc_data;
      } else if (response.status === 400 || response.status === 404 || data.status === 400 || data.status === 404) {
        throw new Error(data.message || "Verification failed or not found");
      }
      
      if (data.data?.rc_data) return data.data.rc_data;
      
      // status 202 = still processing
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error("Verification timed out. Please try again.");
  };

  // Combined fetchRCDetails using above steps
  const fetchRCDetails = async (rcNumber) => {
    setLoading(true);
    setError('');
    setRcData(null);

    try {
      const startResult = await startVerification(rcNumber);
      let rcResult;
      if (startResult.immediateData) {
        rcResult = startResult.immediateData;
      } else {
        rcResult = await pollVerificationResult(startResult);
      }
      setRcData(rcResult);
      logSearchToDatabase(rcNumber, rcResult);
      return rcResult;
    } catch (err) {
      setError(err.message || "Error fetching RC details. Please try again.");
      setRcData(null);
      console.error('Error fetching RC details:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Function to log search to backend database
  const logSearchToDatabase = async (rcNumber, rcData) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch("https://api.rtoxpert.com/api/vehicles/searches", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          vehicle_number: rcNumber,
          rc_data: rcData
        })
      });

      // Silently handle 401 or other errors
      if (!response.ok) {
        if (response.status === 401) {
          console.warn("Failed to log search: Session expired (401).");
        } else {
          console.warn(`Failed to log search: ${response.status}`);
        }
      }
    } catch (error) {
      console.warn("Error logging search:", error);
    }
  };

  // Function to generate PDF from RC details
  const generateRcPdf = () => {
    const doc = new jsPDF();
    let yOffset = 10;

    // Helper function to add section to PDF
    const addSection = (title, fields, startY) => {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(title, 10, startY);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      let y = startY + 10;

      Object.entries(fields).forEach(([label, value]) => {
        if (value) {
          doc.text(`${label}: ${value}`, 10, y);
          y += 7;
        }
      });

      return y;
    };

    // Ownership Details
    const ownershipFields = {
      "Owner Name": rcData?.owner_data?.name || 'N/A',
      "Father's Name": rcData?.owner_data?.father_name || 'N/A',
      "Present Address": rcData?.owner_data?.present_address || 'N/A',
      "Permanent Address": rcData?.owner_data?.permanent_address || 'N/A',
      "Registered RTO": rcData?.registered_at || 'N/A',
      "Financed": rcData?.financed ? 'Yes' : 'No'
    };
    yOffset = addSection("Ownership Details", ownershipFields, yOffset);

    // Vehicle Details
    yOffset += 10;
    const vehicleFields = {
      "Vaahan Make": rcData?.vehicle_data?.maker_description || 'N/A',
      "Vaahan Model": rcData?.vehicle_data?.maker_model || 'N/A',
      "Chassis Number": rcData?.vehicle_data?.chassis_number || 'N/A',
      "Engine Number": rcData?.vehicle_data?.engine_number || 'N/A',
      "Fuel Type": rcData?.vehicle_data?.fuel_type || 'N/A',
      "Body Type": rcData?.vehicle_data?.body_type || 'N/A',
      "Color": rcData?.vehicle_data?.color || 'N/A'
    };
    yOffset = addSection("Vaahan Details", vehicleFields, yOffset);

    // Important Details
    yOffset += 10;
    const importantFields = {
      "Registration Number": rcData?.document_id || 'N/A',
      "Registration Date": rcData?.issue_date || 'N/A',
      "Fitness Valid Upto": rcData?.expiry_date || 'N/A',
      "Insurance Policy Number": rcData?.insurance_data?.policy_number || 'N/A',
      "Insurance Company": rcData?.insurance_data?.company || 'N/A',
      "Insurance Expiry Date": rcData?.insurance_data?.expiry_date || 'N/A',
      "PUC Number": rcData?.pucc_data?.pucc_number || 'N/A',
      "PUC Expiry Date": rcData?.pucc_data?.expiry_date || 'N/A',
      "Tax Validity": rcData?.tax_end_date || 'N/A'
    };
    yOffset = addSection("Important Details", importantFields, yOffset);

    // Other Info
    yOffset += 10;
    const otherFields = {
      "Vaahan Category": rcData?.vehicle_data?.category_description || 'N/A',
      "Cubic Capacity": rcData?.vehicle_data?.cubic_capacity ? `${rcData.vehicle_data.cubic_capacity} CC` : 'N/A',
      "Gross Weight": rcData?.vehicle_data?.gross_weight ? `${rcData.vehicle_data.gross_weight} kg` : 'N/A',
      "Unladen Weight": rcData?.vehicle_data?.unladen_weight ? `${rcData.vehicle_data.unladen_weight} kg` : 'N/A',
      "Number of Cylinders": rcData?.vehicle_data?.number_of_cylinders || 'N/A',
      "Wheelbase": rcData?.vehicle_data?.wheelbase ? `${rcData.vehicle_data.wheelbase} mm` : 'N/A',
      "Norms Type": rcData?.norms_type || 'N/A',
      "Commercial Vaahan": rcData?.is_commercial ? 'Yes' : 'No',
      "Blacklist Status": rcData?.blacklist_status === 'false' ? 'Not Blacklisted' : 'Blacklisted'
    };
    yOffset = addSection("Other Info", otherFields, yOffset);

    // Generate PDF blob
    return doc.output('blob');
  };

  // Function to add vehicle to garage
  const addVehicleToGarage = async (navigateToGarage = false) => {
    if (!rcData) {
      setError('No vaahan details available to add to garage.');
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    setAddingToGarage(true);
    setError('');

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${token}`);

      const formdata = new FormData();
      formdata.append("vehicle_number", rcData.document_id);

      // Determine vehicle type more accurately
      const vehicleCategory = rcData.vehicle_data?.category_description?.toLowerCase() || '';
      const bodyType = rcData.vehicle_data?.body_type?.toLowerCase() || '';
      let vehicleType = 'car';
      if (vehicleCategory.includes('motor cycle') || bodyType.includes('motor cycle') || bodyType.includes('bike') || vehicleCategory.includes('2wn')) {
        vehicleType = 'bike';
      } else if (vehicleCategory.includes('scooter') || bodyType.includes('scooter')) {
        vehicleType = 'scooter';
      }
      formdata.append("vehicle_type", vehicleType);
      formdata.append("brand", rcData.vehicle_data.maker_description || 'Unknown');
      formdata.append("model", rcData.vehicle_data.maker_model || 'Unknown');

      // Extract year
      const issueYear = rcData.issue_date ? new Date(rcData.issue_date).getFullYear() : new Date().getFullYear();
      formdata.append("year_of_registration", issueYear.toString());

      formdata.append("fuel_type", rcData.vehicle_data.fuel_type?.toLowerCase() || 'petrol');
      formdata.append("chassis_number", rcData.vehicle_data.chassis_number || '');
      formdata.append("insurance_status", rcData.insurance_data?.expiry_date ? 'true' : 'false');
      formdata.append("insurance_expiry", rcData.insurance_data?.expiry_date ? new Date(rcData.insurance_data.expiry_date).toISOString().split('T')[0] : '');
      formdata.append("puc_expiry", rcData.pucc_data?.expiry_date ? new Date(rcData.pucc_data.expiry_date).toISOString().split('T')[0] : '');
      formdata.append("fitness_expiry", rcData.expiry_date ? new Date(rcData.expiry_date).toISOString().split('T')[0] : '');
      formdata.append("registration_date", rcData.issue_date ? new Date(rcData.issue_date).toISOString().split('T')[0] : '');
      formdata.append("tax_expiry", rcData.tax_end_date ? new Date(rcData.tax_end_date).toISOString().split('T')[0] : '');
      formdata.append("is_commercial", rcData.is_commercial ? 'true' : 'false');
      formdata.append("permit_expiry", rcData.permit_expiry || '');
      formdata.append("color", rcData.vehicle_data.color || '');
      formdata.append("body_type", rcData.vehicle_data.body_type || '');
      formdata.append("cubic_capacity", rcData.vehicle_data.cubic_capacity || '');
      formdata.append("registered_at", rcData.registered_at || '');
      formdata.append("norms_type", rcData.norms_type || '');
      formdata.append("blacklist_status", rcData.blacklist_status || 'false');

      // Generate and append RC PDF
      const rcPdf = generateRcPdf();
      formdata.append("documents", rcPdf, `RC_${rcData.document_id}.pdf`);
      formdata.append("document_types", "RC");
      formdata.append("expiry_dates", rcData.expiry_date ? new Date(rcData.expiry_date).toISOString().split('T')[0] : '');

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow"
      };

      const response = await fetch("https://api.rtoxpert.com/api/vehicles", requestOptions);
      const result = await response.json();

      if (!response.ok) {
        if (result.message && result.message.includes('already exists')) {
          console.log('Vaahan already exists in garage, skipping add operation');
          setVehicleExistsInGarage(true);
          return;
        }
        throw new Error(`Failed to add vehicle: ${result.message || response.statusText}`);
      }

      console.log('Vaahan added successfully:', result);
      if (navigateToGarage) {
        navigate('/garage');
      }
    } catch (error) {
      console.error('Error adding vehicle to garage:', error);
      if (error.message && error.message.includes('already exists')) {
        console.log('Vehicle already exists in garage, skipping add operation');
        setVehicleExistsInGarage(true);
        return;
      }
      setError(`Failed to add vaahan to garage: ${error.message}`);
    } finally {
      setAddingToGarage(false);
    }
  };

  // Handle auto-add to garage after RC data is fetched
  useEffect(() => {
    if (shouldAutoAddToGarage && rcData && !isAutoAdding && !vehicleExistsInGarage) {
      setIsAutoAdding(true);
      console.log('Auto-adding vaahan to garage...');
      addVehicleToGarage(false).finally(() => {
        setIsAutoAdding(false);
        if (onAutoAddComplete) {
          onAutoAddComplete();
        }
      });
    }
  }, [shouldAutoAddToGarage, rcData, isAutoAdding, vehicleExistsInGarage]);

  // Effect to trigger API call
  useEffect(() => {
    if (shouldFetch && vehicleNumber) {
      setVehicleExistsInGarage(false);
      fetchRCDetails(vehicleNumber);
    }
  }, [shouldFetch, vehicleNumber]);

  // Handle accordion toggling with authentication guard
  const toggleSection = (section) => {
    if (!isAuthenticated && section !== 'Ownership Details') {
      navigate('/phone');
      return;
    }
    setActiveSection(prev => (prev === section ? null : section));
  };

  const handleAddToGarageClick = () => {
    if (!vehicleExistsInGarage) {
      addVehicleToGarage(true);
    } else {
      navigate('/garage');
    }
  };

  // Render sections
  const renderOwnershipDetails = () => {
    if (activeSection !== 'Ownership Details') return null;
    return (
      <div className="accordion-content">
        <div className="input-group">
          <label>Owner Name</label>
          <input type="text" value={maskAlternateCharacters(getRCValue(rcData, ['owner_data.name', 'owner_name', 'rc_owner_name'], ''))} readOnly />
        </div>
        <div className="input-group">
          <label>Father's Name</label>
          <input type="text" value={maskAlternateCharacters(getRCValue(rcData, ['owner_data.father_name', 'father_name', 'rc_f_name']))} readOnly />
        </div>
        <div className="input-group">
          <label>Present Address</label>
          <textarea value={maskAlternateCharacters(getRCValue(rcData, ['owner_data.present_address', 'present_address', 'rc_present_address'], ''))} readOnly rows={3} />
        </div>
        <div className="input-group">
          <label>Permanent Address</label>
          <textarea value={maskAlternateCharacters(getRCValue(rcData, ['owner_data.permanent_address', 'permanent_address', 'rc_permanent_address'], ''))} readOnly rows={3} />
        </div>
        <div className="input-group">
          <label>Registered RTO</label>
          <input type="text" value={getRCValue(rcData, ['rto_name', 'registered_at', 'rto', 'rc_rto_office_code'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Financed</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_financier', 'financing_authority', 'rc_financer'], rcData?.financed ? 'Yes' : 'No')} readOnly />
        </div>
      </div>
    );
  };

  const renderVehicleDetails = () => {
    if (activeSection !== 'Vaahan Details' || !isAuthenticated) return null;
    return (
      <div className="accordion-content">
        <div className="input-group">
          <label>Vaahan Make</label>
          <input type="text" value={getRCValue(rcData, ['manufacturer', 'vehicle_data.maker_description', 'maker_description', 'rc_maker_desc'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Vaahan Model</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.maker_model', 'maker_model', 'rc_maker_model'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Chassis Number</label>
          <input type="text" value={maskLastFiveCharacters(getRCValue(rcData, ['vehicle_data.chassis_number', 'chassis_number', 'rc_chasi_no'], ''))} readOnly />
        </div>
        <div className="input-group">
          <label>Engine Number</label>
          <input type="text" value={maskLastFiveCharacters(getRCValue(rcData, ['vehicle_data.engine_number', 'engine_number', 'rc_eng_no'], ''))} readOnly />
        </div>
        <div className="input-group">
          <label>Fuel Type</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.fuel_type', 'fuel_type', 'rc_fuel_desc'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Body Type</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.body_type', 'body_type', 'rc_body_type_desc'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Color</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.color', 'color', 'rc_color'], '')} readOnly />
        </div>
      </div>
    );
  };

  const renderImportantDetails = () => {
    if (activeSection !== 'Important Details' || !isAuthenticated) return null;
    return (
      <div className="accordion-content">
        <div className="input-group">
          <label>Registration Number</label>
          <input type="text" value={getRCValue(rcData, ['registration_number', 'document_id', 'vehicle_number', 'rc_regn_no'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Registration Date</label>
          <input type="text" value={getRCValue(rcData, ['issue_date', 'registration_date', 'rc_regn_dt'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Fitness Valid Upto</label>
          <input type="text" value={getRCValue(rcData, ['fitness_upto', 'expiry_date', 'fitness_expiry', 'rc_fit_upto'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Insurance Policy Number</label>
          <input type="text" value={maskFullString(getRCValue(rcData, ['insurance_data.policy_number', 'insurance_policy_no', 'rc_insurance_policy_no'], ''))} readOnly />
        </div>
        <div className="input-group">
          <label>Insurance Company</label>
          <input type="text" value={getRCValue(rcData, ['insurance_data.company', 'insurance_company', 'rc_insurance_comp'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Insurance Expiry Date</label>
          <input type="text" value={getRCValue(rcData, ['insurance_validity', 'insurance_data.expiry_date', 'insurance_expiry', 'rc_insurance_upto'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>PUC Number</label>
          <input type="text" value={maskLastFiveCharacters(getRCValue(rcData, ['pucc_data.pucc_number', 'pucc_number', 'rc_pucc_no'], ''))} readOnly />
        </div>
        <div className="input-group">
          <label>PUC Expiry Date</label>
          <input type="text" value={getRCValue(rcData, ['pucc_data.expiry_date', 'pucc_expiry', 'rc_pucc_upto'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Tax Validity</label>
          <input type="text" value={getRCValue(rcData, ['mv_tax_upto', 'tax_end_date', 'tax_expiry', 'rc_tax_upto'], '')} readOnly />
        </div>
      </div>
    );
  };

  const renderOtherInfo = () => {
    if (activeSection !== 'Other Info' || !isAuthenticated) return null;
    return (
      <div className="accordion-content">
        <div className="input-group">
          <label>Vaahan Category</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.category_description', 'category_description', 'rc_vch_catg'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Cubic Capacity</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.cubic_capacity', 'cubic_capacity', 'rc_cubic_cap']) ? `${getRCValue(rcData, ['vehicle_data.cubic_capacity', 'cubic_capacity', 'rc_cubic_cap'])} CC` : ''} readOnly />
        </div>
        <div className="input-group">
          <label>Gross Weight</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.gross_weight', 'gross_weight', 'rc_gvw']) ? `${getRCValue(rcData, ['vehicle_data.gross_weight', 'gross_weight', 'rc_gvw'])} kg` : ''} readOnly />
        </div>
        <div className="input-group">
          <label>Unladen Weight</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.unladen_weight', 'unladen_weight', 'rc_unld_wt']) ? `${getRCValue(rcData, ['vehicle_data.unladen_weight', 'unladen_weight', 'rc_unld_wt'])} kg` : ''} readOnly />
        </div>
        <div className="input-group">
          <label>Number of Cylinders</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.number_of_cylinders', 'number_of_cylinders', 'rc_no_cyl'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Wheelbase</label>
          <input type="text" value={getRCValue(rcData, ['vehicle_data.wheelbase', 'wheelbase', 'rc_wheelbase']) ? `${getRCValue(rcData, ['vehicle_data.wheelbase', 'wheelbase', 'rc_wheelbase'])} mm` : ''} readOnly />
        </div>
        <div className="input-group">
          <label>Norms Type</label>
          <input type="text" value={getRCValue(rcData, ['norms_type', 'norms_desc', 'rc_norms_desc'], '')} readOnly />
        </div>
        <div className="input-group">
          <label>Commercial Vaahan</label>
          <input type="text" value={getRCValue(rcData, ['is_commercial']) ? 'Yes' : 'No'} readOnly />
        </div>
        <div className="input-group">
          <label>Blacklist Status</label>
          <input type="text" value={(getRCValue(rcData, ['blacklist_status', 'rc_blacklist_status']) === 'false' || getRCValue(rcData, ['blacklist_status', 'rc_blacklist_status']) === false) ? 'Not Blacklisted' : 'Blacklisted'} readOnly />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="rc-container">
        <div className="loading-message">
          <h2>Loading RC Details...</h2>
          <p>Please wait while we fetch the vaahan information.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rc-container">
        <div className="error-message">
          <h2>Error</h2>
          <p style={{ color: 'red' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!rcData) {
    return (
      <div className="rc-container">
        <h2 className="rc-title">RC Details</h2>
        <p>Enter a vaahan number and click search to view RC details.</p>
      </div>
    );
  }

  return (
    <div className="rc-container">
      <h2 className="rc-title">RC Details - {rcData.document_id}</h2>

      {vehicleExistsInGarage ? (
        <div className="vehicle-exists-message" style={{
          backgroundColor: '#e8f5e8',
          border: '1px solid #4CAF50',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          <p style={{ color: '#2e7d32', margin: '0 0 10px 0', fontWeight: 'bold' }}>
            ✓ Vaahan already exists in your garage
          </p>
          <button
            className="action-btn"
            onClick={() => navigate('/garage')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            View in Garage
          </button>
        </div>
      ) : (
        <div className="button-group" style={{ marginBottom: '20px' }}>
          <button
            className="action-btn"
            onClick={handleAddToGarageClick}
            disabled={!rcData || addingToGarage || !isAuthenticated}
            style={{
              padding: '10px 20px',
              backgroundColor: !isAuthenticated || addingToGarage ? '#cccccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: !isAuthenticated || addingToGarage ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {addingToGarage ? 'Adding...' : 'Add To Garage'}
          </button>
        </div>
      )}

      {addingToGarage && <p style={{ color: "green", marginBottom: "10px" }}>Adding vaahan to garage...</p>}
      {isAutoAdding && <p style={{ color: "blue", marginBottom: "10px" }}>Vaahan automatically added to garage!</p>}
      {error && <p style={{ color: "red", marginBottom: "10px" }}>{error}</p>}

      {/* Check Challans Button */}
      <div className="button-group" style={{ marginBottom: '20px' }}>
        <button
          className="check-challan-btn"
          onClick={() => navigate(`/checkChallan?vehicleNumber=${encodeURIComponent(vehicleNumber)}`)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#DC143C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: '300px',
            transition: 'background-color 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#B8102E'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#DC143C'}
        >
          <span role="img" aria-label="challan">📋</span>
          Check Challans
        </button>
      </div>

      <div className="accordion">
        <div>
          <div className="accordion-item" onClick={() => toggleSection('Ownership Details')}>
            Ownership Details
            <span className="accordion-icon">{activeSection === 'Ownership Details' ? '-' : '+'}</span>
          </div>
          {renderOwnershipDetails()}
        </div>
        <div>
          <div className="accordion-item" onClick={() => toggleSection('Vaahan Details')}>
            Vaahan Details
            <span className="accordion-icon">{activeSection === 'Vaahan Details' ? '-' : '+'}</span>
          </div>
          {renderVehicleDetails()}
        </div>
        <div>
          <div className="accordion-item" onClick={() => toggleSection('Important Details')}>
            Important Details
            <span className="accordion-icon">{activeSection === 'Important Details' ? '-' : '+'}</span>
          </div>
          {renderImportantDetails()}
        </div>
        <div>
          <div className="accordion-item" onClick={() => toggleSection('Other Info')}>
            Other Info
            <span className="accordion-icon">{activeSection === 'Other Info' ? '-' : '+'}</span>
          </div>
          {renderOtherInfo()}
        </div>
      </div>
    </div>
  );
}

export default RCDetails;
